import React from "react";

// Multi-series stacked bars. `series` should already be filtered to only the
// currently-visible keys (toggle logic lives in the caller); `scaleMax` is passed
// in separately so the axis scale stays stable while series are toggled on/off.
export default function StackedBarChart({ data, series, height = 180, scaleMax, valueFormatter }) {
  const fmt = valueFormatter || ((v) => v);
  const rowTotals = data.map((d) => series.reduce((a, s) => a + (d.values[s.key] || 0), 0));
  const max = scaleMax ?? Math.max(...rowTotals, 1) * 1.15;

  if (series.length === 0) {
    return (
      <div className="flex items-center justify-center text-xs text-[#9aa0a6]" style={{ height }}>
        Select at least one status to display.
      </div>
    );
  }

  return (
    <div className="flex items-end justify-between gap-1.5 sm:gap-2" style={{ height }}>
      {data.map((d, i) => {
        const total = rowTotals[i];
        const stackPct = max > 0 ? Math.min(100, (total / max) * 100) : 0;
        return (
          <div key={i} className="flex-1 h-full flex flex-col items-center justify-end gap-1.5 min-w-0">
            <span className="text-[10px] font-semibold text-[#3c4043] dark:text-[#dadce0] h-3.5">{total > 0 ? fmt(total) : ""}</span>
            <div className="w-full flex-1 flex items-end justify-center relative">
              {/* faint full-height track so a zero-value bucket still reads as "no data" rather than "missing" */}
              <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-[28px] sm:max-w-[36px] h-full rounded-t-md bg-[#f1f3f4] dark:bg-[#202124]/70" />
              <div
                className="relative w-full max-w-[28px] sm:max-w-[36px] flex flex-col-reverse rounded-t-md overflow-hidden transition-all duration-500"
                style={{ height: `${stackPct}%` }}
              >
                {series.map((s) => {
                  const v = d.values[s.key] || 0;
                  if (v === 0) return null;
                  const segPct = total > 0 ? (v / total) * 100 : 0;
                  return (
                    <div
                      key={s.key}
                      title={`${s.label}: ${v}`}
                      className="w-full border-t border-white/40 dark:border-black/20 first:border-t-0"
                      style={{ height: `${segPct}%`, backgroundColor: s.color }}
                    />
                  );
                })}
              </div>
            </div>
            <span className="text-[10px] text-[#9aa0a6] text-center truncate w-full">{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}
